import axios from 'axios';
import React, { useEffect, useState } from 'react';
import './Pricing.css';

// Razorpay type declaration
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface SubscriptionData {
  id: number;
  plan_type: string;
  status: string;
  is_pro: boolean;
  is_starter: boolean;
  projects_limit: number;
  plans_per_project_limit: number;
  ai_generations_limit: number;
  ai_generations_used: number;
  start_date: string;
  end_date: string | null;
}

const Pricing: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  // Sync with theme changes
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      setIsDarkMode(savedTheme === "dark");
    };

    // Initial check
    checkTheme();

    // Listen for storage changes
    window.addEventListener("storage", checkTheme);

    // Check periodically for theme changes
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  // Pricing structure
  const starterMonthlyPrice = 999;
  const starterYearlyPrice = 9999;
  const proMonthlyPrice = 2000;
  const proYearlyPrice = 20000;

  const starterYearlyDiscount = Math.round(((starterMonthlyPrice * 12 - starterYearlyPrice) / (starterMonthlyPrice * 12)) * 100);
  const proYearlyDiscount = Math.round(((proMonthlyPrice * 12 - proYearlyPrice) / (proMonthlyPrice * 12)) * 100);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch current subscription on component mount
  useEffect(() => {
    fetchSubscription();
  }, []);

  const getAuthToken = () => {
    return localStorage.getItem('access_token');
  };

  const fetchSubscription = async () => {
    const token = getAuthToken();
    if (!token) {
      // console.log('No auth token found');
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/subscription/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubscription(response.data);
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleUpgrade = async (planTier: 'starter' | 'pro') => {
    const token = getAuthToken();

    if (!token) {
      setError('Please log in to upgrade your plan');
      window.location.href = '/LoginPage?redirect=/pricing';
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create subscription in backend
      const response = await axios.post(
        `${API_BASE_URL}/subscription/create-subscription/`,
        {
          plan_type: billingCycle,
          plan_tier: planTier
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const {
        subscription_id,
        db_subscription_id,
        razorpay_key,
        customer,
        description,
        plan_type,
        plan_tier: responsePlanTier
      } = response.data;

      // Step 2: Open Razorpay Checkout
      const options = {
        key: razorpay_key,
        subscription_id: subscription_id,
        name: 'SOORU.AI',
        description: description,
        image: '/logo.png',
        prefill: {
          name: customer.name,
          email: customer.email,
          contact: customer.contact
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            // console.log('Checkout form closed');
          }
        },
        handler: async function (response: any) {
          // Step 3: Verify payment on backend
          await verifyPayment(
            response.razorpay_payment_id,
            response.razorpay_subscription_id,
            response.razorpay_signature,
            plan_type,
            db_subscription_id,
            responsePlanTier
          );
        }
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', function (response: any) {
        setError('Payment failed. Please try again.');
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
        setLoading(false);
        console.error('Payment failed:', response.error);
      });

      razorpay.open();

    } catch (err: any) {
      console.error('Error creating subscription:', err);
      setError(
        err.response?.data?.error ||
        'Failed to initiate payment. Please try again.'
      );
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      setLoading(false);
    }
  };

  const verifyPayment = async (
    payment_id: string,
    subscription_id: string,
    signature: string,
    plan_type: string,
    db_subscription_id: string,
    plan_tier: string
  ) => {
    const token = getAuthToken();

    try {
      const response = await axios.post(
        `${API_BASE_URL}/subscription/verify-payment/`,
        {
          razorpay_payment_id: payment_id,
          razorpay_subscription_id: subscription_id,
          razorpay_signature: signature,
          db_subscription_id: db_subscription_id,
          plan_type: plan_type,
          plan_tier: plan_tier
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Payment verified successfully
      setLoading(false);

      // Show success message
      // alert(`Payment successful! Your ${plan_tier.charAt(0).toUpperCase() + plan_tier.slice(1)} subscription is now active.`);

      // Refresh subscription data
      await fetchSubscription();

      // Redirect to dashboard
      window.location.href = '/dashboard';

    } catch (err: any) {
      console.error('Error verifying payment:', err);
      setError(
        err.response?.data?.error ||
        'Failed to verify payment. Please contact support.'
      );
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      setLoading(false);
    }
  };

  const handleGetStartedFree = () => {
    const token = getAuthToken();
    if (!token) {
      window.location.href = '/signup';
    } else {
      window.location.href = '/projects';
    }
  };

  const isCurrentPlan = (planType: string) => {
    if (!subscription) return false;

    if (planType === 'free') {
      return subscription.plan_type === 'free';
    } else if (planType === 'starter') {
      return subscription.plan_type === "starter_monthly" || subscription.plan_type === "starter_yearly"
    } else if (planType === 'pro') {
      return subscription.plan_type === "pro_monthly" || subscription.plan_type === "pro_yearly"
    }
    return false;
  };

  return (
    <div className={`pricing-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Background Gradient - Removed, moved to hero */}
      {loading && (
        <div className="pricing-page-loading-message">
          {/* This new div will be the content box */}
          <div className="pricing-page-loading-content-box">
            <div className="pricing-page-spinner"></div>
            <p>Processing your Payment...</p>
          </div>
        </div>
      )}
      {/* Breadcrumb */}
      <div className="pricing-breadcrumb">
        <a href="/">Home</a> / <span>Pricing</span>
      </div>

      {/* Hero Section */}
      <section className="pricing-hero">
        <h1 className="pricing-page-title">
          Simple, <br />
          <span className="highlight">Transparent Pricing.</span>
        </h1>
        <p className="pricing-page-subtitle">
          Choose the plan that's right for you<br />
          and start building today.
        </p>

        {/* Billing Toggle */}
        <div className="pricing-page-billing-toggle-container">
          <span className={`pricing-page-billing-label ${billingCycle === 'monthly' ? 'active' : ''}`}>
            Monthly
          </span>
          <button
            className="pricing-page-billing-toggle"
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            aria-label="Toggle billing cycle"
            disabled={loading}
          >
            <div className={`pricing-page-toggle-slider ${billingCycle === 'yearly' ? 'yearly' : ''}`}></div>
          </button>
          <span className={`pricing-page-billing-label ${billingCycle === 'yearly' ? 'active' : ''}`}>
            Yearly
            <span className="pricing-page-discount-badge">Save up to {proYearlyDiscount}%</span>
          </span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="pricing-page-error-message">
            {error}
            <button
              className="pricing-page-error-close"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        )}
      </section>

      {/* Pricing Cards */}
      <div className="pricing-page-cards-container">
        {/* Free Plan */}
        <div className={`pricing-page-card pricing-page-free-plan ${isCurrentPlan('free') ? 'current-plan' : ''}`}>
          <div className="pricing-page-plan-badge">
            {isCurrentPlan('free') ? 'Current Plan' : 'Free Forever'}
          </div>
          <h2 className="pricing-page-plan-name">Free</h2>
          <div className="pricing-page-plan-price">
            <span className="pricing-page-price">₹0</span>
            <span className="pricing-page-price-period">/month</span>
          </div>
          <p className="pricing-page-plan-description">Perfect for trying out our platform</p>

          <ul className="pricing-page-features-list">
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Up to 2 projects</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>3 plans per project</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>10 AI generations/month</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Basic floor plan tools</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Export as PNG</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Community support</span>
            </li>
          </ul>

          <button
            className="pricing-page-plan-button pricing-page-free-button"
            onClick={handleGetStartedFree}
            disabled={isCurrentPlan('free') || loading}
          >
            {isCurrentPlan('free') ? 'Current Plan' : 'Get Started Free'}
          </button>
        </div>

        {/* Starter Plan */}
        <div className={`pricing-page-card pricing-page-starter-plan ${isCurrentPlan('starter') ? 'current-plan' : ''}`}>
          <div className="pricing-page-plan-badge pricing-page-popular-badge">
            {isCurrentPlan('starter') ? 'Current Plan' : 'Most Popular'}
          </div>
          <h2 className="pricing-page-plan-name">Starter</h2>
          <div className="pricing-page-plan-price">
            <span className="pricing-page-price">
              ₹{billingCycle === 'monthly' ? starterMonthlyPrice : Math.round(starterYearlyPrice / 12)}
            </span>
            <span className="pricing-page-price-period">
              /month
            </span>
          </div>
          {billingCycle === 'yearly' && (
            <p className="pricing-page-billing-info">Billed ₹{starterYearlyPrice} annually</p>
          )}
          <p className="pricing-page-plan-description">Great for individuals and small teams</p>

          <ul className="pricing-page-features-list">
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>Up to 10 projects</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>10 plans per project</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>100 AI generations/month</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Advanced floor plan tools</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Export as PNG, PDF, DXF</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Email support</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Custom branding</span>
            </li>
          </ul>

          <button
            className="pricing-page-plan-button pricing-page-starter-button"
            onClick={() => handleUpgrade('starter')}
            disabled={loading || isCurrentPlan('starter')}
          >
            {loading ? 'Processing...' : isCurrentPlan('starter') ? 'Current Plan' : 'Upgrade to Starter'}
          </button>
        </div>

        {/* Pro Plan */}
        <div className={`pricing-page-card pricing-page-pro-plan ${isCurrentPlan('pro') ? 'current-plan' : ''}`}>
          <div className="pricing-page-plan-badge pricing-page-enterprise-badge">
            {isCurrentPlan('pro') ? 'Current Plan' : 'Best Value'}
          </div>
          <h2 className="pricing-page-plan-name">Pro</h2>
          <div className="pricing-page-plan-price">
            <span className="pricing-page-price">
              ₹{billingCycle === 'monthly' ? proMonthlyPrice : Math.round(proYearlyPrice / 12)}
            </span>
            <span className="pricing-page-price-period">
              /month
            </span>
          </div>
          {billingCycle === 'yearly' && (
            <p className="pricing-page-billing-info">Billed ₹{proYearlyPrice} annually</p>
          )}
          <p className="pricing-page-plan-description">For professionals who need unlimited access</p>

          <ul className="pricing-page-features-list">
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>Unlimited projects</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>Unlimited plans per project</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span><strong>Unlimited AI generations</strong></span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Advanced floor plan tools</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Export all formats + 3D models</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Priority support (24/7)</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>Team collaboration</span>
            </li>
            <li className="pricing-page-feature-item">
              <span className="pricing-page-check-icon">✓</span>
              <span>API access</span>
            </li>
          </ul>

          <button
            className="pricing-page-plan-button pricing-page-pro-button"
            onClick={() => handleUpgrade('pro')}
            disabled={loading || isCurrentPlan('pro')}
          >
            {loading ? 'Processing...' : isCurrentPlan('pro') ? 'Current Plan' : 'Upgrade to Pro'}
          </button>
          {billingCycle === 'yearly' && !isCurrentPlan('pro') && (
            <p className="pricing-page-savings-text">🎉 Save ₹{proMonthlyPrice * 12 - proYearlyPrice} per year</p>
          )}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="pricing-page-comparison-section">
        <h2 className="pricing-page-comparison-title">Compare Plans</h2>
        <div className="pricing-page-comparison-table-wrapper">
          <table className="pricing-page-comparison-table">
            <thead>
              <tr>
                <th>Features</th>
                <th>Free</th>
                <th>Starter</th>
                <th>Pro</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Projects</td>
                <td>2</td>
                <td>10</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td>Plans per Project</td>
                <td>3</td>
                <td>10</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td>AI Generations</td>
                <td>10/month</td>
                <td>100/month</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td>Export Formats</td>
                <td>PNG</td>
                <td>PNG, PDF, DXF</td>
                <td>All + 3D</td>
              </tr>
              <tr>
                <td>Support</td>
                <td>Community</td>
                <td>Email</td>
                <td>Priority 24/7</td>
              </tr>
              <tr>
                <td>Collaboration</td>
                <td>-</td>
                <td>-</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>API Access</td>
                <td>-</td>
                <td>-</td>
                <td>✓</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="pricing-page-faq">
        <h2 className="pricing-page-faq-title">Frequently Asked Questions</h2>
        <div className="pricing-page-faq-grid">
          <div className="pricing-page-faq-item">
            <h3 className="pricing-page-faq-question">Can I switch plans anytime?</h3>
            <p className="pricing-page-faq-answer">
              Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div className="pricing-page-faq-item">
            <h3 className="pricing-page-faq-question">What payment methods do you accept?</h3>
            <p className="pricing-page-faq-answer">
              We accept all major credit cards, debit cards, UPI, Net Banking, and wallets through Razorpay.
            </p>
          </div>
          <div className="pricing-page-faq-item">
            <h3 className="pricing-page-faq-question">Is there a free trial for paid plans?</h3>
            <p className="pricing-page-faq-answer">
              Start with our Free plan to explore the platform, then upgrade to Starter or Pro when you're ready for more features.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="pricing-page-cta">
        <h2 className="pricing-page-cta-title">Ready to get started?</h2>
        <p className="pricing-page-cta-subtitle">Join thousands of professionals creating amazing floor plans</p>
        <button
          className="pricing-page-cta-button"
          onClick={handleGetStartedFree}
          disabled={loading}
        >
          Start Building Today
        </button>
      </div>
    </div >
  );
};

export default Pricing;